import { useState, useRef, useEffect, useCallback } from 'react';
import type { EmailOption, SendStatus } from './types';
import { validateEmail } from './constants';
import { logger } from '@/utils/logger';

interface UseEmailSendOptions {
  onClose: () => void;
  onSendEmail: (email: string) => Promise<void>;
  registeredEmail?: string;
}

export function useEmailSend({ onClose, onSendEmail, registeredEmail }: UseEmailSendOptions) {
  const [selectedOption, setSelectedOption] = useState<EmailOption>(null);
  const [newEmail, setNewEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedOption(null);
    setNewEmail('');
    setIsValidEmail(true);
    setIsSending(false);
    setSendStatus('idle');
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setNewEmail(email);
    if (email) {
      setIsValidEmail(validateEmail(email));
    } else {
      setIsValidEmail(true);
    }
  }, []);

  const handleSelectRegistered = useCallback(() => {
    setSelectedOption('registered');
    setNewEmail('');
    setIsValidEmail(true);
  }, []);

  const handleSelectNew = useCallback(() => {
    setSelectedOption('new');
    setIsValidEmail(true);
  }, []);

  const handleSend = useCallback(async () => {
    let emailToSend = '';

    if (selectedOption === 'registered') {
      if (!registeredEmail) {
        setIsValidEmail(false);
        return;
      }
      emailToSend = registeredEmail;
    } else if (selectedOption === 'new') {
      if (!newEmail || !validateEmail(newEmail)) {
        setIsValidEmail(false);
        return;
      }
      emailToSend = newEmail;
    }

    setIsSending(true);
    setSendStatus('idle');

    try {
      await onSendEmail(emailToSend);
      setSendStatus('success');

      setTimeout(() => {
        onClose();
        resetState();
      }, 2000);
    } catch (error) {
      logger.error('Email send error:', error);
      setSendStatus('error');
    } finally {
      setIsSending(false);
    }
  }, [selectedOption, registeredEmail, newEmail, onSendEmail, onClose, resetState]);

  const handleClose = useCallback(() => {
    if (!isSending) {
      onClose();
      resetState();
    }
  }, [isSending, onClose, resetState]);

  // Auto-focus input when 'new' option is selected
  useEffect(() => {
    if (selectedOption === 'new' && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [selectedOption]);

  const canSend = selectedOption !== null &&
    !isSending &&
    !(selectedOption === 'new' && (!newEmail || !isValidEmail));

  return {
    selectedOption,
    newEmail,
    isValidEmail,
    isSending,
    sendStatus,
    inputRef,
    canSend,
    handleEmailChange,
    handleSelectRegistered,
    handleSelectNew,
    handleSend,
    handleClose
  };
}
